import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = ['trigger'];

    static values = {
        withToggleText: Boolean,
        openToggleText: { type: String, default: 'Afficher' },
        closeToggleText: { type: String, default: 'Masquer' },
        withoutAutoClose: Boolean,
        storageKey: String,
    };

    connect() {
        this.boundTransitionEnd = (event) => {
            event.target.style.height = '';
        };
        this.initializeTriggers();
        this.loadStoredActiveTriggers();
    }

    initializeTriggers() {
        this.triggerTargets.forEach((trigger) => {
            const content = this.findContent(trigger);

            if (this.triggerIsActive(trigger)) {
                this.open(trigger, content, false);
            } else {
                this.close(trigger, content, false);
            }
        });
    }

    toggle(event) {
        this.triggerTargets.forEach((trigger) => {
            if (trigger.contains(event.target)) {
                this.toggleState(trigger);
            } else if (this.triggerIsActive(trigger) && !this.withoutAutoCloseValue) {
                this.toggleState(trigger);
            }
        });

        event.preventDefault();
    }

    toggleState(trigger) {
        const content = this.findContent(trigger);
        const action = this.triggerIsActive(trigger) ? 'close' : 'open';

        this[action](trigger, content);
        this.storeActiveTriggers();
    }

    toggleContent(trigger, content, isActive, withTransition = true) {
        if (withTransition) {
            this.applyToggleTransition(trigger, content, isActive);
        }

        this.applyToggleClasses(trigger, content, isActive);
        this.applyToggleText(trigger, isActive);
    }

    open(trigger, content, withTransition = true) {
        this.toggleContent(trigger, content, true, withTransition);
    }

    close(trigger, content, withTransition = true) {
        this.toggleContent(trigger, content, false, withTransition);
    }

    triggerIsActive(trigger) {
        return trigger.classList.contains('accordion__trigger--is-active');
    }

    findContent(trigger) {
        const contentId = trigger.getAttribute('aria-controls');

        return this.element.querySelector(`#${CSS.escape(contentId)}`);
    }

    applyToggleClasses(trigger, content, isActive) {
        trigger.classList.toggle('accordion__trigger--is-active', isActive);
        content.classList.toggle('accordion__content--is-visible', isActive);
    }

    applyToggleText(trigger, isActive) {
        if (!this.withToggleTextValue) {
            return;
        }

        const toggleText = trigger.querySelector('.accordion__toggle-text');
        if (!toggleText) {
            return;
        }

        toggleText.textContent = isActive ? this.closeToggleTextValue : this.openToggleTextValue;
    }

    applyToggleTransition(trigger, content, isActive) {
        content.style.height = isActive ? '0px' : `${content.scrollHeight}px`;
        content.removeEventListener('transitionend', this.boundTransitionEnd);
        content.addEventListener('transitionend', this.boundTransitionEnd);

        setTimeout(() => {
            content.style.height = isActive ? `${content.scrollHeight}px` : '0px';
        });
    }

    storeActiveTriggers() {
        if (!this.hasStorageKeyValue) {
            return;
        }

        const activeTriggers = this.triggerTargets
            .filter((trigger) => this.triggerIsActive(trigger))
            .map((trigger) => trigger.getAttribute('aria-controls'));

        sessionStorage.setItem(this.storageKeyValue, JSON.stringify(activeTriggers));
    }

    loadStoredActiveTriggers() {
        if (!this.hasStorageKeyValue) {
            return;
        }

        const stored = sessionStorage.getItem(this.storageKeyValue);
        if (!stored) {
            return;
        }

        const storedActiveTriggers = JSON.parse(stored);

        this.triggerTargets.forEach((trigger) => {
            const content = this.findContent(trigger);
            const isActive = storedActiveTriggers.includes(trigger.getAttribute('aria-controls'));

            if (isActive) {
                this.open(trigger, content, false);
            }
        });
    }
}
